import clsx from 'clsx';

import type * as api from '@gitbook/api-client';
import { checkIsFreeProductKey, getProductDefinition } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { Badge, type BadgeProps } from '@gitbook/spine-react';

export type OrganizationPlanBadge = {
    plan: api.BillingProduct;
    trialing?: boolean;
} & Omit<BadgeProps, 'kind' | 'label'>;

/**
 * Get the props to create a badge for an organization's plan.
 * Returns `null` if the badge must not be displayed.
 */
export function getOrganizationPlanBadgeProps(props: OrganizationPlanBadge): BadgeProps | null {
    const { plan, trialing, ...otherProps } = props;
    const def = getProductDefinition(plan);

    if (!def) {
        return null;
    }

    if (trialing) {
        return {
            ...otherProps,
            label: 'Trialing',
            kind: 'upgrade',
            icon: Icon.Upgrade,
            className: clsx('cursor-default', otherProps.className),
        };
    }

    return {
        ...otherProps,
        label: def.title,
        kind: checkIsFreeProductKey(def.key) ? 'secondary' : undefined,
    };
}

/**
 * Badge for an organization's plan.
 */
export function OrganizationPlanBadge(props: OrganizationPlanBadge) {
    const badgeProps = getOrganizationPlanBadgeProps(props);
    if (!badgeProps) {
        return null;
    }
    return <Badge {...badgeProps} />;
}
